import { useAuth } from '@agentnet/auth';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { GetFileInfoProps } from 'api/file/file-api';
import { updateFincenReporteDateAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { useMenuContext } from '../ReportingLayout';
import { FincenResponse } from '../types';
import FilingInformationContent from './FilingInformationContent';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      position: 'relative',
      overflowX: 'hidden',
    },
    contentWrapper: {
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingBottom: theme.spacing(8),
    },
  }),
);

const FilingInformationContainer: FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const { menuCollapsed } = useMenuContext();
  const classes = useStyles({ menuCollapsed });
  const { getAccessToken } = useAuth();
  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { fincenReport, fincenReportStatus } = fincenReportCtx ?? {};

  // Load file data when lost on refresh
  useEffect(() => {
    if (fileId && fileDataContext && !fileDataContext.fileData?.fileId && fileDataContext.loadFileData) {
      const fileProps: GetFileInfoProps = {
        fileId,
      };
      fileDataContext.loadFileData(fileProps);
    }
  }, [fileId, fileDataContext]);

  const [fincenData, setFincenData] = useState<FincenResponse | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [shouldNavigateOnComplete, setShouldNavigateOnComplete] = useState(false);
  const isNavigating = useNavigationLoading();

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport?.filingInformation) return;
    if (fincenReport.reportStatusCdId === 9302) return; // Disable auto-save once report generated

    const token = await getAccessToken();
    await updateFincenReporteDateAPI(String(fincenReport.fincenReportId || 0), fincenReport.filingInformation, token);
  }, [fincenReport, getAccessToken]);

  useAutoSaveNavigation('filing-information', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: fincenReport?.filingInformation,
    updateFunction: undefined, // No local state update function needed
    apiSaveFunction: updateFincenReporteDateAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => !!(data && data.datePrepared),
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'filing information',
  });

  const handleSaveReport = () => {
    setFormSubmitted(true);
  };

  const handleFormSubmissionComplete = (forceNavigate?: boolean) => {
    setFormSubmitted(false);
    if (forceNavigate || shouldNavigateOnComplete) {
      history.push(`/files/${fileId}/fincen-reporting/report-party`);
      setShouldNavigateOnComplete(false);
    }
  };

  const handleNext = () => {
    if (fincenReport?.reportStatusCdId === 9302) {
      history.push(`/files/${fileId}/fincen-reporting/report-party`);
      return;
    }
    setShouldNavigateOnComplete(true);
    setFormSubmitted(true);
  };

  // Redirect to files if fileId is not present (handles browser refresh edge case)
  if (!fileId) {
    return <Redirect to="/files" />;
  }

  return (
    <div className={classes.container}>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <div className={classes.contentWrapper}>
        <FilingInformationContent
          onDataLoaded={setFincenData}
          onFormSubmitted={handleFormSubmissionComplete}
          submitted={formSubmitted}
        />
      </div>
      <BottomAppBar
        primaryActionLabel="Next"
        secondaryActionLabel="Save Report"
        onPrimaryClick={handleNext}
        onSecondaryClick={handleSaveReport}
        primaryActionDataQa="fincen-filing-next-button"
        secondaryActionDataQa="fincen-filing-save-button"
        hideCancel={true}
        disableSecondary={fincenReport?.reportStatusCdId === 9302}
      />
    </div>
  );
};

export default FilingInformationContainer;
